/**
 * Simple region detection based on ZIP codes, IP geolocation, or user address
 * Returns a standardized region name for market insights
 */

// Major metropolitan area ZIP code ranges (simplified mapping)
const ZIP_TO_REGION = {
  // New York Metro
  '100': 'NEW YORK', '101': 'NEW YORK', '102': 'NEW YORK', '103': 'NEW YORK', '104': 'NEW YORK',
  '110': 'NEW YORK', '111': 'NEW YORK', '112': 'NEW YORK', '113': 'NEW YORK', '114': 'NEW YORK',
  
  // Los Angeles Metro
  '900': 'LOS ANGELES', '901': 'LOS ANGELES', '902': 'LOS ANGELES', '903': 'LOS ANGELES', '904': 'LOS ANGELES',
  '905': 'LOS ANGELES', '906': 'LOS ANGELES', '907': 'LOS ANGELES', '908': 'LOS ANGELES',
  
  // San Francisco Bay Area
  '940': 'SAN FRANCISCO', '941': 'SAN FRANCISCO', '942': 'SAN FRANCISCO', '943': 'SAN FRANCISCO', '944': 'SAN FRANCISCO',
  '945': 'SAN FRANCISCO', '946': 'SAN FRANCISCO', '947': 'SAN FRANCISCO', '948': 'SAN FRANCISCO',
  
  // Chicago Metro
  '600': 'CHICAGO', '601': 'CHICAGO', '602': 'CHICAGO', '603': 'CHICAGO', '604': 'CHICAGO', '605': 'CHICAGO', '606': 'CHICAGO', '607': 'CHICAGO', '608': 'CHICAGO',
  
  // Seattle Metro
  '980': 'SEATTLE', '981': 'SEATTLE', '982': 'SEATTLE', '983': 'SEATTLE', '984': 'SEATTLE',
  
  // Boston Metro
  '021': 'BOSTON', '022': 'BOSTON', '023': 'BOSTON', '024': 'BOSTON', '025': 'BOSTON',
  
  // Dallas Metro
  '750': 'DALLAS', '751': 'DALLAS', '752': 'DALLAS', '753': 'DALLAS', '754': 'DALLAS', '755': 'DALLAS',
  
  // Atlanta Metro
  '300': 'ATLANTA', '301': 'ATLANTA', '302': 'ATLANTA', '303': 'ATLANTA', '304': 'ATLANTA', '305': 'ATLANTA',
  
  // St. Louis Metro
  '630': 'ST. LOUIS', '631': 'ST. LOUIS', '632': 'ST. LOUIS', '633': 'ST. LOUIS', '634': 'ST. LOUIS', '635': 'ST. LOUIS',
};

/**
 * Extract ZIP code from various address formats
 * @param {string} address - Full address string
 * @returns {string|null} - 5-digit ZIP code or null
 */
function extractZipFromAddress(address) {
  if (!address || typeof address !== 'string') return null;
  
  // Look for 5-digit ZIP codes in various formats
  const zipMatch = address.match(/\b(\d{5})\b/);
  return zipMatch ? zipMatch[1] : null;
}

/**
 * Detect region from ZIP code
 * @param {string} zip - 5-digit ZIP code
 * @returns {string} - Region name or 'NATIONAL' as fallback
 */
function detectRegionFromZip(zip) {
  if (!zip || zip.length !== 5) return 'NATIONAL';
  
  // Check 3-digit prefix first
  const prefix3 = zip.substring(0, 3);
  if (ZIP_TO_REGION[prefix3]) return ZIP_TO_REGION[prefix3];
  
  // Check 2-digit prefix for broader matches
  const prefix2 = zip.substring(0, 2);
  if (ZIP_TO_REGION[prefix2]) return ZIP_TO_REGION[prefix2];
  
  return 'NATIONAL';
}

/**
 * Primary region detection function
 * @param {Object} options - Detection options
 * @param {string} options.address - User-provided address
 * @param {string} options.zip - Explicit ZIP code
 * @param {string} options.fallback - Fallback region
 * @returns {string} - Detected region name
 */
export function detectCurrentRegion({ address, zip, fallback = 'NATIONAL' } = {}) {
  // Try explicit ZIP first
  if (zip) {
    const region = detectRegionFromZip(zip);
    if (region !== 'NATIONAL') return region;
  }
  
  // Try extracting ZIP from address
  if (address) {
    const extractedZip = extractZipFromAddress(address);
    if (extractedZip) {
      const region = detectRegionFromZip(extractedZip);
      if (region !== 'NATIONAL') return region;
    }
  }
  
  return fallback;
}

/**
 * Get region display name (formatted for UI)
 * @param {string} region - Region code
 * @returns {string} - Formatted region name
 */
export function getRegionDisplayName(region) {
  if (!region || region === 'NATIONAL') return 'NATIONAL';
  
  // Convert to title case for display
  return region
    .split(' ')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Check if region is a major metro (higher costs/availability)
 * @param {string} region - Region code
 * @returns {boolean} - True if major metro area
 */
export function isMajorMetro(region) {
  const majorMetros = ['NEW YORK', 'LOS ANGELES', 'SAN FRANCISCO', 'BOSTON', 'SEATTLE'];
  return majorMetros.includes(region);
}